import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, BellRing, Check, RefreshCw, Send } from '../../icons';
import {
  Badge, Button, EmptyState, Input, PageHeader, Select, Table, Tabs, Toolbar,
  type TableColumn,
  StatTile,
  StatTiles
} from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import {
  listLeavePromotions, sendLeavePromotion, recordPromotionResponse,
  type PromotionRowDto, type PromotionStatus,
} from '../../api/hr-admin';
import './AttendanceAdmin.css';
import './LeavePromotion.css';

/**
 * **연차 사용촉진** — 근로기준법 §61 (2026-08-30 · 판 J).
 *
 * ## 이 화면이 목록이 아닌 이유
 *
 * ★★ 촉진은 **「했다」는 증빙이 있어야** 미사용 연차 수당 지급 의무가 면제된다. 「덜 쓴 사람」을
 *    보여 주는 것만으로는 회사가 아무것도 증명하지 못한다. 그래서 이 화면의 단추는 **보낸다 =
 *    알린다 + 표에 한 행을 박는다** 이고, 박힌 행은 지우거나 덮어쓰지 않는다.
 *
 * ## 두 번의 통보 (1년 이상)
 *
 *   1. **1차 촉구** — 사용기간이 끝나기 6개월 전을 기준으로 10일 이내. «언제 쓸지 알려 달라».
 *   2. **2차 지정** — 근로자가 안 알려 주면 회사가 시기를 정해 끝나기 2개월 전까지.
 *
 * 1년 미만(§61②)은 같은 절차인데 기한이 **3개월 전 / 1개월 전**이다 — 화면이 그 사람을 따로 표시한다.
 *
 * ⚠ 1년 미만의 **마지막 2일분**은 법이 한 번 더 촉구를 요구하는데 여기서는 다루지 않는다.
 *   감추지 않고 화면이 적어서 알린다 — 모르고 넘어가는 것이 가장 나쁘다.
 *
 * ── 정본 채택 (2026-09-06 · 채택 3단계 ③ 근태 · 연차 넷) ─────────────────────────
 * `PageHeader` · `Tabs`(pill · 건수) · `Toolbar` · `Select` · `Table`(rowHeader · 고르기) ·
 * `Badge` · `EmptyState` · `Button` · `Input`.
 *
 * ★★ **법 조문은 도움말로 갔다**(정본 P8) — 「1차는 6개월 전 10일 이내」는 촉진을 백 번 열어도
 *    백 번 같은 말이다. 대신 **이 회사의 회차**(입사일 기준이냐 · 회계연도 몇 월 시작이냐)는
 *    회사마다 다르고 기한의 출발점이라 **화면 머리에 남겼다**.
 * ★★★ **「마지막 2일분」 경고는 화면에 남겼다.** 이것만은 P8 의 예외다 — 담당자가 이 화면이
 *    그 회차를 안 다룬다는 것을 **모르면 법정 의무를 빠뜨린다.** 눌러야 보이는 자리에 두면
 *    안 본 채로 통보하게 된다(이상 근태의 「조회 기록 고지」와 같은 판단).
 * ★★ **줄마다 고르기를 잠그는 것**이 정본 `Table` 에 없어 `rowSelectable` 을 세웠다 —
 *    안 세웠으면 「보낼 수 없는 사람도 골라진다」는 회귀였다.
 */

/**
 * ★ **이 표가 문구의 정본이다** (2026-09-24 사용자 결정 — 「화면에 보이는 글자를 정본으로」).
 *
 * 서버에도 같은 표가 있다(`apps/chat-server/src/modules/hr/leave-promotion.ts`
 * `PROMOTION_STATUS_LABEL`). 그쪽은 부르는 곳이 없어 **화면에 나가는 것은 여기 글자**다.
 * ⇒ 문구를 고치면 **그쪽도 따라 맞춘다**(2026-09-24 에 `DUE_FIRST`·`DUE_SECOND` 가 벌어져 있었다).
 * ⚠ `NO_PERIOD` 는 **여기에만** 있다 — 서버 키에는 없다.
 */
const STATUS_LABEL: Readonly<Record<PromotionStatus, string>> = {
  NOT_NEEDED: '해당 없음',
  BEFORE: '아직 이름',
  DUE_FIRST: '1차 촉구',
  LATE_FIRST: '1차 기한 지남',
  WAIT_RESPONSE: '회신 대기',
  DUE_SECOND: '2차 지정',
  LATE_SECOND: '2차 기한 지남',
  DONE: '완료',
  NO_PERIOD: '회차 없음',
};

/**
 * 색은 «지금 해야 하나»로 가른다 — 상태 이름이 아니라 **급함**을 보여 주는 것이 목적이다.
 * ★ 정본 `Badge` 로 옮겼다(#957 에서 tint/ink 짝으로 고쳐져 대비가 4.5 를 넘는다).
 */
const STATUS_VARIANT: Readonly<Record<PromotionStatus, 'default' | 'warning' | 'danger' | 'info' | 'success'>> = {
  NOT_NEEDED: 'default',
  BEFORE: 'default',
  DUE_FIRST: 'warning',
  LATE_FIRST: 'danger',
  WAIT_RESPONSE: 'info',
  DUE_SECOND: 'warning',
  LATE_SECOND: 'danger',
  DONE: 'success',
  NO_PERIOD: 'default',
};

/** 지금 무엇을 보낼 수 있나 — 상태가 곧 답이다. */
function sendableStage(s: PromotionStatus): 'NOTICE' | 'DESIGNATION' | null {
  if (s === 'DUE_FIRST' || s === 'LATE_FIRST') return 'NOTICE';
  if (s === 'DUE_SECOND' || s === 'LATE_SECOND') return 'DESIGNATION';
  return null;
}

const ERRORS: Record<string, string> = {
  FORBIDDEN: '이 화면은 근태 담당자만 쓸 수 있습니다.',
  NO_TARGET: '대상 직원을 찾을 수 없습니다.',
};

type Filter = 'todo' | 'all';

const CHANNELS = [
  { value: 'APP', label: '사내 알림' },
  { value: 'PAPER', label: '서면 — 기록만' },
];

export function LeavePromotionPage(): JSX.Element {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [rows, setRows] = useState<PromotionRowDto[]>([]);
  const [basis, setBasis] = useState('HIRE_DATE');
  const [startMonth, setStartMonth] = useState(1);
  const [picked, setPicked] = useState<string[]>([]);
  const [channel, setChannel] = useState<'APP' | 'PAPER'>('APP');
  const [filter, setFilter] = useState<Filter>('todo');
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [replyDates, setReplyDates] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listLeavePromotions(year);
      setRows(res.rows);
      setBasis(res.basis);
      setStartMonth(res.fiscalStartMonth);
      setPicked([]);
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setError(ERRORS[code ?? ''] ?? '불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { void load(); }, [load]);

  const counts = useMemo(() => {
    const c = { todo: 0, wait: 0, done: 0, late: 0 };
    for (const r of rows) {
      if (r.status === 'DUE_FIRST' || r.status === 'DUE_SECOND') c.todo += 1;
      if (r.status === 'WAIT_RESPONSE') c.wait += 1;
      if (r.status === 'DONE') c.done += 1;
      if (r.status === 'LATE_FIRST' || r.status === 'LATE_SECOND') c.late += 1;
    }
    return c;
  }, [rows]);

  // ★ 기본은 «할 일만» 이다 — 전 직원을 늘어놓으면 정작 오늘 해야 할 사람이 묻힌다.
  const shown = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => sendableStage(r.status) !== null || r.status === 'WAIT_RESPONSE')),
    [rows, filter],
  );

  /** 고른 사람들 중 **같은 단계로 보낼 수 있는** 사람만 센다(1차와 2차를 한 번에 보내지 않는다). */
  const pickedFor = useCallback(
    (stage: 'NOTICE' | 'DESIGNATION') =>
      rows.filter((r) => picked.includes(r.userId) && sendableStage(r.status) === stage).map((r) => r.userId),
    [rows, picked],
  );

  const onSend = async (stage: 'NOTICE' | 'DESIGNATION') => {
    const userIds = pickedFor(stage);
    if (userIds.length === 0) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const res = await sendLeavePromotion({ year, stage, userIds, channel });
      const label = stage === 'NOTICE' ? '1차 촉구' : '2차 지정 통보';
      const skip = res.skipped.length > 0
        ? ` · 건너뜀 ${res.skipped.length}명(${res.skipped.map((s) => `${s.name}: ${s.reason}`).join(', ')})`
        : '';
      setDone(`${label} ${res.sent}명 기록했습니다${channel === 'PAPER' ? ' — 서면이라 알림은 보내지 않았습니다' : ''}${skip}`);
      await load();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setError(ERRORS[code ?? ''] ?? '보내지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const onReply = async (noticeId: string) => {
    setBusy(true);
    setError(null);
    try {
      const dates = replyDates.split(',').map((s) => s.trim()).filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));
      await recordPromotionResponse(noticeId, dates.length > 0 ? { plannedDates: dates } : {});
      setDone('회신을 기록했습니다.');
      setReplyFor(null);
      setReplyDates('');
      await load();
    } catch {
      setError('회신을 기록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const pickTodo = () => setPicked(rows.filter((r) => sendableStage(r.status)).map((r) => r.userId));

  /**
   * 회차가 실제로 걸치는 폭 (2026-09-06 · 육안 지적).
   *
   * ★★★ **「2026년 회차」라고만 적으면 «2026년 안에 끝나는 것»으로 읽힌다.** 입사일 기준에서는
   *    4월 입사자의 2026년 연차가 **2027-03-31 까지** 쓰는 것이고 촉진 기한도 거기 딸려 간다 —
   *    법대로인데 화면만 보면 「내년까지 촉진하나?」가 된다. 그래서 **실제 폭을 적는다**
   *    (지어낸 말이 아니라 지금 이 목록이 무엇인가 · 정본 P8).
   */
  const span = useMemo(() => {
    const starts = rows.map((r) => r.periodStart).filter((v): v is string => !!v);
    const ends = rows.map((r) => r.periodEnd).filter((v): v is string => !!v);
    if (starts.length === 0 || ends.length === 0) return null;
    return {
      from: starts.reduce((a, b) => (a < b ? a : b)),
      to: ends.reduce((a, b) => (a > b ? a : b)),
    };
  }, [rows]);

  /** 아직 아무도 차례가 아닐 때 **언제부터인지**를 말한다 — 빈 화면이 고장으로 읽히지 않게. */
  const nextFirst = useMemo(() => {
    const ds = rows.filter((r) => r.status === 'BEFORE' && r.firstFrom).map((r) => r.firstFrom as string);
    return ds.length > 0 ? ds.reduce((a, b) => (a < b ? a : b)) : null;
  }, [rows]);

  const noticeCount = pickedFor('NOTICE').length;
  const designationCount = pickedFor('DESIGNATION').length;

  const columns: ReadonlyArray<TableColumn<PromotionRowDto>> = useMemo(() => [
    {
      key: 'name',
      header: '이름',
      width: '200px',
      rowHeader: true,
      render: (r) => (
        <>
          {r.name}
          {r.teamName && <span className="hra__dim"> · {r.teamName}</span>}
          {/* 1년 미만은 **기한이 다르다** — 붙여 두지 않으면 같은 자로 재게 된다. */}
          {r.underOneYear && <Badge size="sm">1년 미만</Badge>}
        </>
      ),
    },
    {
      key: 'period',
      header: '사용기간',
      width: '190px',
      render: (r) => (
        <span className={r.periodStart ? undefined : 'is-muted'}>
          {r.periodStart ? `${r.periodStart} ~ ${r.periodEnd}` : '입사일 미상'}
        </span>
      ),
    },
    {
      key: 'unused',
      header: '미사용',
      width: '80px',
      align: 'right',
      render: (r) => (
        <span className={r.unusedDays && r.unusedDays > 0 ? 'is-strong' : 'is-muted'}>
          {r.unusedDays === null ? '—' : `${r.unusedDays}일`}
        </span>
      ),
    },
    {
      key: 'notice',
      header: '1차 촉구',
      render: (r) => (r.notice ? (
        <div className="lvpr__rec">
          <b>{r.notice.sentAt.slice(0, 10)}</b>
          <span className="hra__dim">{r.notice.unusedDays}일 · {r.notice.channel === 'PAPER' ? '서면' : '알림'}</span>
          {r.notice.respondedAt ? (
            <span className="lvpr__ok">회신 {r.notice.respondedAt.slice(0, 10)}</span>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setReplyFor(r.notice!.id); setReplyDates(''); }}
            >
              회신 기록
            </Button>
          )}
          {replyFor === r.notice.id && (
            <span className="lvpr__reply">
              <Input
                size="sm"
                wrapperClassName="hra__reasonbox"
                aria-label={`${r.name} 사용 예정일`}
                placeholder="2026-11-03, 2026-11-04"
                value={replyDates}
                onChange={(e) => setReplyDates(e.target.value)}
              />
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => { void onReply(r.notice!.id); }}>
                적기
              </Button>
            </span>
          )}
        </div>
      ) : (
        <span className="hra__dim">{r.firstFrom ? `${r.firstFrom} ~ ${r.firstTo}` : '—'}</span>
      )),
    },
    {
      key: 'designation',
      header: '2차 지정',
      width: '150px',
      render: (r) => (r.designation ? (
        <div className="lvpr__rec">
          <b>{r.designation.sentAt.slice(0, 10)}</b>
          <span className="hra__dim">{r.designation.unusedDays}일</span>
        </div>
      ) : (
        <span className="hra__dim">{r.secondBy ? `${r.secondBy}까지` : '—'}</span>
      )),
    },
    {
      key: 'status',
      header: '상태',
      width: '112px',
      render: (r) => <Badge variant={STATUS_VARIANT[r.status]} size="sm">{STATUS_LABEL[r.status]}</Badge>,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [replyFor, replyDates, busy]);

  return (
    <div className="hra">
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance/leave-promotion')}
        scope={scopeFor('/erp/attendance/leave-promotion')}
        title="연차촉진"
        /*
          ★ 회차가 언제 시작하는지는 **회사마다 다르고** 모든 기한의 출발점이다 — 화면에 남는다.
          ★★★ 그리고 **어디까지 걸치는지**도 함께 적는다 — 입사일 기준이면 회차가 해를 넘겨서
             「2026년 회차인데 왜 2027년 날짜가 나오나」가 된다(2026-09-06 육안 지적).
        */
        description={
          basis === 'FISCAL_YEAR'
            ? `${year}년 회차 · 회계연도 기준 · ${startMonth}월 시작${span ? ` · ${span.from} ~ ${span.to}` : ''}`
            : `${year}년에 «시작하는» 회차 · 입사일 기준이라 사람마다 다릅니다${span ? ` — ${span.from} ~ ${span.to} 에 걸쳐 있고, 촉진 기한도 각자의 회차를 따라갑니다` : ''}`
        }
        help={{
          title: '법이 정한 두 번 (근기법 §61)',
          body: (
            <>
              <p><b>1차 촉구</b> — 사용기간이 끝나기 <b>6개월 전</b> 기준 10일 이내. 남은 일수를
                알리고 «언제 쓸지 정해 달라».</p>
              <p><b>근로자 회신</b> — 촉구받은 날부터 10일 이내.</p>
              <p><b>2차 지정</b> — 회신이 없으면 회사가 시기를 정해 <b>2개월 전</b>까지 통보.</p>
              <p>1년 미만 근로자는 기한이 다릅니다 — <b>3개월 전 / 1개월 전</b>.</p>
              <p><b>보낸 기록은 고치거나 지우지 않습니다</b> — 통보 시각이 밀리면 기한을 지켰다는
                증빙이 무너집니다. 남는 일수도 <b>그때의 값</b>을 그대로 박아 둡니다.
                보낸 것은 감사 기록에도 남습니다.</p>
            </>
          ),
        }}
        actions={(
          <Select
            ariaLabel="연도 선택"
            size="sm"
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
            /* ★ 「2026년」이 아니라 「2026년 시작」이다 — 고르개에서부터 뜻을 못박는다. */
            options={[year + 1, year, year - 1, year - 2].map((y) => ({ value: String(y), label: `${y}년 시작` }))}
          />
        )}
      />

      {/* 정본 D9-2 요약 타일 (2026-09-06 신설) — 손짜기 `hra__stat` 에서 옮겨 왔다. */}
      <StatTiles ariaLabel="촉진 현황">
        <StatTile label="지금 보낼 것" value={counts.todo} unit="명" />
        <StatTile label="회신 대기" value={counts.wait} unit="명" />
        {/* ★ 법정 기한을 넘긴 것이다 — 색조는 «급함»에만 쓴다(정본 D9-2). */}
        <StatTile label="기한 지남" value={counts.late} unit="명" tone={counts.late > 0 ? 'danger' : 'default'} />
        <StatTile label="완료" value={counts.done} unit="명" />
      </StatTiles>

      {/*
        ★★★ **이것만은 도움말로 안 보낸다.** 담당자가 「이 화면이 그 회차를 안 다룬다」를
        모르면 법정 의무를 통째로 빠뜨린다 — 눌러야 보이는 자리에 두면 안 본 채로 통보한다.
      */}
      <p className="hra__warn" role="note">
        <AlertCircle size={14} aria-hidden />
        1년 미만 근로자의 <b>마지막 2일분</b>은 법이 한 번 더 촉구를 요구합니다(1개월 전 기준 5일
        이내). 이 화면은 그 회차를 따로 다루지 않으니, 1차 촉구에 함께 안내해 주세요.
      </p>

      {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {done && <p className="hra__ok" role="status"><Check size={14} aria-hidden />{done}</p>}

      <Toolbar
        actions={(
          <>
            <Button variant="secondary" onClick={pickTodo} disabled={busy || counts.todo === 0}>
              보낼 사람 모두 고르기
            </Button>
            <Select
              ariaLabel="통보 방식"
              className="lvpr__channelbox"
              size="sm"
              value={channel}
              onChange={(v) => setChannel(v as 'APP' | 'PAPER')}
              options={CHANNELS}
            />
          </>
        )}
      >
        {/* ★ 칩에 «몇 명 보이나»를 적는다 — 전체 기준 숫자는 거짓말이 된다(예약 화면에서 배운 것). */}
        <Tabs
          variant="pill"
          ariaLabel="보기"
          value={filter}
          onChange={(v) => setFilter(v as Filter)}
          items={[
            { value: 'todo', label: '할 일', count: counts.todo + counts.wait },
            { value: 'all', label: '전체', count: rows.length },
          ]}
        />
      </Toolbar>

      <Table
        caption="연차촉진 대상"
        className="hra-tbl"
        columns={columns}
        rows={shown}
        rowKey={(r) => r.userId}
        loading={loading}
        selectedKeys={picked}
        onSelectionChange={setPicked}
        rowSelectionLabel={(r) => `${r.name} 고르기`}
        // ★★ 보낼 수 있는 사람만 골라진다 — 못 보낼 사람을 골라 놓고 단추를 눌러도
        //    아무 일이 안 일어나면, 「눌렀는데 안 됐다」가 된다.
        rowSelectable={(r) => sendableStage(r.status) !== null}
        empty={filter === 'todo'
          ? (
            <EmptyState
              icon={Check}
              title="지금 보낼 사람이 없습니다."
              /* ★ 빈 화면이 «고장»으로 읽히지 않게 «언제부터인지»를 말한다(2026-09-06 육안 지적). */
              description={nextFirst
                ? `기한이 온 사람도, 회신을 기다리는 사람도 없습니다 — 가장 이른 1차 촉구는 ${nextFirst} 부터입니다.`
                : '기한이 온 사람도, 회신을 기다리는 사람도 없습니다.'}
              action={<Button variant="secondary" size="sm" onClick={() => setFilter('all')}>전체 보기</Button>}
            />
          )
          : <EmptyState title="직원이 없습니다." description="연차 회차를 가진 직원이 아직 없습니다." />}
      />

      <footer className="hra__foot">
        <div className="hra__actions">
          <Button variant="secondary" iconLeft={<RefreshCw size={14} aria-hidden />} onClick={() => { void load(); }} disabled={busy}>
            다시 불러오기
          </Button>
          <Button
            iconLeft={<BellRing size={14} aria-hidden />}
            disabled={busy || noticeCount === 0}
            onClick={() => { void onSend('NOTICE'); }}
          >
            1차 촉구 {noticeCount > 0 ? `${noticeCount}명` : ''}
          </Button>
          <Button
            iconLeft={<Send size={14} aria-hidden />}
            disabled={busy || designationCount === 0}
            onClick={() => { void onSend('DESIGNATION'); }}
          >
            2차 지정 {designationCount > 0 ? `${designationCount}명` : ''}
          </Button>
        </div>
      </footer>
    </div>
  );
}

export default LeavePromotionPage;
